import React from 'react';
import { Check, Upload, ArrowRight, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Select } from '../../ui/Field';
import { pillPrimary, pillSecondary } from '../../ui/pills';
import { CHAMPS_CIBLES, ChampCible, FichierLu, appliquerMapping, lettreColonne, lireFichierConvives } from './convivesFileParser';
import type { ImportResult, ParsedRow } from './useConvivesActions';

/** Ce que l'organisateur a déjà dans sa liste — pour annoncer les doublons AVANT d'importer (maquette 19c). */
export interface ConviveExistant { email: string; invite: boolean }

/**
 * Assistant d'import (maquette 18c, « ConvivesImportModal · assistant · 520 ») — trois étapes dans le même
 * cadre, jamais une modale sur une autre (passation §5) : 1 choisir le fichier, 2 associer les colonnes,
 * 3 le résultat. Le fichier est lu dans le navigateur ; seules les lignes nettoyées partent au serveur,
 * dont les règles ne changent pas : une ligne sans adresse est écartée, une adresse déjà présente est
 * ignorée (pas remplacée — la maquette l'écrivait autrement, l'appli dédoublonne par adresse).
 *
 * Variante « organisateur » (Portail Organisateur, maquette 19c « OrganisateurImportModal ») : mêmes trois
 * étapes, mais l'étape 2 est un BILAN à vérifier (retenus, sans adresse, doublons) plutôt que l'association
 * des colonnes — celle-ci ne s'affiche que si le fichier n'a pas été reconnu tout seul.
 */
export function ConvivesImportModal({ importing, onImport, onClose, variante = 'etablissement', existants = [] }: {
    importing: boolean;
    onImport: (rows: ParsedRow[], fileName: string) => Promise<ImportResult>;
    onClose: () => void;
    variante?: 'etablissement' | 'organisateur';
    existants?: ConviveExistant[];
}) {
    const orga = variante === 'organisateur';
    const [lu, setLu] = React.useState<FichierLu | null>(null);
    const [mapping, setMapping] = React.useState<Record<string, ChampCible>>({});
    const [erreur, setErreur] = React.useState('');
    const [lecture, setLecture] = React.useState(false);
    const [resultat, setResultat] = React.useState<ImportResult | null>(null);

    const etape = resultat ? 3 : lu ? 2 : 1;

    const choisir = async (file: File | null) => {
        if (!file) return;
        setErreur('');
        setLecture(true);
        try {
            const f = await lireFichierConvives(file);
            setLu(f);
            setMapping(f.mapping);
        } catch (e: any) {
            setErreur(e?.message || 'Impossible de lire ce fichier.');
        } finally {
            setLecture(false);
        }
    };

    const lignes = React.useMemo(() => (lu ? appliquerMapping(lu, mapping) : []), [lu, mapping]);
    const retenues = lignes.filter((r) => r.email && (r.last_name || r.first_name));
    const sansAdresse = lu ? lignes.length - lignes.filter((r) => r.email).length : 0;
    const adresseManquante = !!lu && !lu.columns.some((k) => mapping[k] === 'email');
    const nomManquant = !!lu && !lu.columns.some((k) => mapping[k] === 'last_name' || mapping[k] === 'first_name');
    // Le bilan de l'organisateur : doublons à l'intérieur du fichier, et adresses déjà dans sa liste.
    const bilan = React.useMemo(() => {
        const vues = new Set<string>();
        const connus = new Map(existants.map((e) => [e.email.trim().toLowerCase(), e.invite]));
        let dansFichier = 0, dejaPresents = 0, dejaInvites = 0, prets = 0;
        retenues.forEach((r) => {
            const k = r.email.trim().toLowerCase();
            if (vues.has(k)) { dansFichier++; return; }
            vues.add(k);
            if (connus.has(k)) { dejaPresents++; if (connus.get(k)) dejaInvites++; return; }
            prets++;
        });
        return { prets, sansAdresse, dansFichier, dejaPresents, dejaInvites };
    }, [retenues, existants, sansAdresse]);
    const aImporter = orga ? bilan.prets : retenues.length;
    const montrerColonnes = !orga || adresseManquante || nomManquant;

    const importer = async () => {
        setErreur('');
        try {
            setResultat(await onImport(retenues, lu?.fileName || ''));
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'import.");
        }
    };

    const sousTitre = etape === 1 ? 'Étape 1 sur 3 — choisir le fichier'
        : etape === 2 ? (orga ? "Étape 2 sur 3 — vérifier avant d'importer" : 'Étape 2 sur 3 — associer les colonnes')
        : 'Étape 3 sur 3 — terminé';

    return (
        <Modal open onClose={onClose} size="md" title={orga ? 'Importer vos convives' : 'Importer la liste des convives'} subtitle={sousTitre}
            footerLayout="split"
            footer={
                etape === 1 ? (
                    <>
                        <span />
                        <button type="button" onClick={onClose} className={pillSecondary()}>Annuler</button>
                    </>
                ) : etape === 2 ? (
                    <>
                        <button type="button" onClick={() => { setLu(null); setErreur(''); }} className={pillSecondary()} disabled={importing}>Retour</button>
                        <button type="button" onClick={importer} className={pillPrimary()} disabled={importing || adresseManquante || nomManquant || aImporter === 0}>
                            {importing ? <Loader2 size={13} className="animate-spin" /> : null}
                            Importer {aImporter} convive{aImporter > 1 ? 's' : ''}
                        </button>
                    </>
                ) : (
                    <>
                        <span />
                        <button type="button" onClick={onClose} className={pillPrimary()}>Fermer</button>
                    </>
                )
            }>
            {etape === 1 && (
                <div className="space-y-3.5">
                    <label className="block cursor-pointer">
                        <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0] || null; e.target.value = ''; choisir(f); }} />
                        <div className="border border-dashed border-border rounded-[10px] p-6 flex flex-col items-center justify-center gap-1.5 hover:border-content hover:bg-surface-soft transition-colors">
                            {lecture ? <Loader2 size={22} className="animate-spin text-content-muted" /> : <Upload size={22} className="text-content-muted" />}
                            <p className="text-[12.5px] font-semibold text-content">{orga ? 'Choisir votre fichier' : 'Choisir le fichier du client'}</p>
                            <p className="text-[11.5px] text-content-muted">{orga ? 'CSV ou Excel — Nom, Prénom et Email' : "CSV ou Excel — les colonnes s'associent à l'étape suivante"}</p>
                        </div>
                    </label>
                    <p className="text-[12px] leading-[1.5] text-content-muted text-pretty">
                        {orga ? <>Les colonnes attendues sont <span className="font-mono">Nom</span>, <span className="font-mono">Prénom</span> et <span className="font-mono">Email</span>. Un convive déjà invité n'est jamais dupliqué : sa ligne est ignorée.</>
                            : <>Le fichier ne contient que <span className="font-mono">Nom</span>, <span className="font-mono">Prénom</span> et <span className="font-mono">Email</span>.
                        Le plat, la contrainte alimentaire et le refus viennent ensuite du convive lui-même.</>}
                    </p>
                    {erreur && <p className="text-[12px] text-state-danger font-semibold">{erreur}</p>}
                </div>
            )}

            {etape === 2 && lu && (
                <div className="space-y-3.5">
                    <div className="flex items-center gap-[11px] px-[13px] py-[11px] rounded-[10px] bg-surface-soft border border-border-soft">
                        <span className="w-7 h-7 shrink-0 rounded-[7px] bg-state-ok-soft border border-state-ok/30 text-state-ok flex items-center justify-center"><Check size={14} /></span>
                        <div className="min-w-0">
                            <div className="font-mono text-[12px] font-medium text-content truncate">{lu.fileName}</div>
                            <div className="text-[11.5px] text-content-muted">{lu.rows.length} ligne{lu.rows.length > 1 ? 's' : ''} lue{lu.rows.length > 1 ? 's' : ''} · {lu.columns.length} colonne{lu.columns.length > 1 ? 's' : ''} {orga ? 'reconnue' : 'détectée'}{lu.columns.length > 1 ? 's' : ''}</div>
                        </div>
                    </div>

                    {orga && (
                        <div className="space-y-[7px]">
                            {([
                                [bilan.prets, `convive${bilan.prets > 1 ? 's' : ''} prêt${bilan.prets > 1 ? 's' : ''} à être importé${bilan.prets > 1 ? 's' : ''}`, 'ok'],
                                [bilan.sansAdresse, `ligne${bilan.sansAdresse > 1 ? 's' : ''} sans adresse électronique — écartée${bilan.sansAdresse > 1 ? 's' : ''}`, 'warn'],
                                [bilan.dansFichier + bilan.dejaPresents, `doublon${bilan.dansFichier + bilan.dejaPresents > 1 ? 's' : ''} déjà présent${bilan.dansFichier + bilan.dejaPresents > 1 ? 's' : ''} dans la liste — ignoré${bilan.dansFichier + bilan.dejaPresents > 1 ? 's' : ''}`, 'warn'],
                                [bilan.dejaInvites, `convive${bilan.dejaInvites > 1 ? 's' : ''} déjà invité${bilan.dejaInvites > 1 ? 's' : ''} ne sera${bilan.dejaInvites > 1 ? 'ont' : ''} pas modifié${bilan.dejaInvites > 1 ? 's' : ''}`, 'neutre'],
                            ] as [number, string, string][]).map(([n, label, ton], i) => {
                                const t = n === 0 ? 'neutre' : ton;
                                const cls = t === 'ok' ? 'bg-state-ok-soft border-state-ok/30 text-state-ok' : t === 'warn' ? 'bg-state-warn-soft border-state-warn/30 text-state-warn' : 'bg-surface-soft border-border-soft text-content-muted';
                                return (
                                    <div key={i} className={`flex items-center gap-2.5 px-[11px] py-[9px] rounded-[9px] border ${cls}`}>
                                        <span className="text-[15px] font-bold tabular-nums shrink-0 min-w-[26px]">{n}</span>
                                        <span className="text-[12.5px] min-w-0">{label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {montrerColonnes && <div className="space-y-[9px]">
                        {lu.columns.map((col, i) => {
                            const ignoree = mapping[col] === 'ignore';
                            return (
                                <div key={col} className="grid grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)] items-center gap-[9px]">
                                    <span className="h-9 px-[11px] flex items-center rounded-[9px] bg-surface-soft border border-border font-mono text-[12px] text-content-muted min-w-0 truncate" title={col}>
                                        Colonne {lettreColonne(i)} — « {col.replace(/^﻿/, '')} »
                                    </span>
                                    <ArrowRight size={15} className="text-content-muted shrink-0" />
                                    <Select value={mapping[col]} onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value as ChampCible }))}
                                        className={`!h-9 font-semibold ${ignoree ? '!border-state-warn/40 !text-state-warn' : ''}`}>
                                        {CHAMPS_CIBLES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Select>
                                </div>
                            );
                        })}
                    </div>}

                    <p className="text-[12px] leading-[1.5] text-content-muted text-pretty">
                        {orga
                            ? <>Les colonnes attendues sont <span className="font-mono">Nom</span>, <span className="font-mono">Prénom</span> et <span className="font-mono">Email</span>. Un convive déjà invité n'est jamais dupliqué : sa ligne est ignorée.</>
                            : <>Les lignes sans adresse seront écartées{sansAdresse > 0 ? ` (${sansAdresse} dans ce fichier)` : ''}. Une adresse déjà présente dans la liste sera ignorée plutôt que d'y créer un second convive.</>}
                    </p>
                    {adresseManquante && <p className="text-[12px] text-state-warn font-semibold">Indiquez quelle colonne porte l'adresse électronique : c'est par elle que part l'invitation.</p>}
                    {!adresseManquante && nomManquant && <p className="text-[12px] text-state-warn font-semibold">Indiquez au moins la colonne du nom ou du prénom.</p>}
                    {erreur && <p className="text-[12px] text-state-danger font-semibold">{erreur}</p>}
                </div>
            )}

            {etape === 3 && resultat && (
                <div className="space-y-2">
                    <div className="flex items-center gap-[11px] px-[13px] py-[11px] rounded-[10px] bg-state-ok-soft border border-state-ok/30">
                        <span className="w-7 h-7 shrink-0 rounded-[7px] bg-surface border border-state-ok/30 text-state-ok flex items-center justify-center"><Check size={14} /></span>
                        <div className="text-[13px] font-bold text-state-ok">{resultat.imported} convive{resultat.imported > 1 ? 's' : ''} importé{resultat.imported > 1 ? 's' : ''}</div>
                    </div>
                    {(resultat.duplicates_skipped > 0 || resultat.invalid_skipped > 0) && (
                        <p className="text-[12px] text-content-muted">
                            {resultat.duplicates_skipped > 0 && `${resultat.duplicates_skipped} adresse${resultat.duplicates_skipped > 1 ? 's' : ''} déjà présente${resultat.duplicates_skipped > 1 ? 's' : ''} ignorée${resultat.duplicates_skipped > 1 ? 's' : ''}`}
                            {resultat.duplicates_skipped > 0 && resultat.invalid_skipped > 0 && ' · '}
                            {resultat.invalid_skipped > 0 && `${resultat.invalid_skipped} ligne${resultat.invalid_skipped > 1 ? 's' : ''} sans adresse écartée${resultat.invalid_skipped > 1 ? 's' : ''}`}
                        </p>
                    )}
                    <p className="text-[12px] text-content-muted text-pretty">{orga ? "Les invitations ne sont pas encore parties : c'est le geste suivant, « Envoyer la demande de menu »." : 'Les invitations ne sont pas encore parties : envoyez-les depuis la liste quand la date de clôture est réglée.'}</p>
                </div>
            )}
        </Modal>
    );
}
